import { defineCollection } from "@rebasepro/cms-types";
import { policy } from "@rebasepro/types";
import customersCollection from "./customers.js";

const transactionsCollection = defineCollection({
    name: "Transactions",
    singularName: "Transaction",
    slug: "transactions",
    table: "transactions",
    description: "Every purchase or return event from Shopify, Odoo POS, or manual entry. Drives the lifetime points engine.",
    history: true,
    // The database's own guard against counting one sale, refund or manual
    // correction twice. The handlers look before they write; these catch two
    // deliveries arriving at once, which both look and both find nothing.
    //
    // Purchases are keyed on the order and returns on the REFUND: one invoice
    // can have several partial refunds, all carrying its order id, and keying
    // returns on that swallowed the second one (see
    // backend/drizzle/idempotency-indexes.sql for that history).
    //
    // Declared here so the deployment's boot creates them. They used to be a
    // hand-applied SQL file, which no managed database ever got: on staging a
    // repeated points correction counted twice (2026-10-07). A unique index
    // the existing rows violate stops the boot, so duplicates must be removed
    // before deploying to a database that has some.
    indexes: [
        {
            on: ["external_order_id", "source"],
            unique: true,
            where: { and: [{ prop: "type", op: "=", value: "purchase" }, { prop: "external_order_id", op: "is not null" }] },
            reason: "a purchase is recorded once per order and source, however often it is delivered"
        },
        {
            on: ["external_refund_id", "source"],
            unique: true,
            where: { and: [{ prop: "type", op: "=", value: "return" }, { prop: "external_refund_id", op: "is not null" }] },
            reason: "a return is recorded once per refund and source, however often it is delivered"
        }
    ],
    securityRules: [
        { operation: "select", access: "public" },
        { operations: ["insert", "update", "delete"], roles: ["admin"] },
        // Los flujos internos de servidor (webhooks de Odoo/Shopify, motor de
        // niveles, emision de pases) corren sin usuario autenticado, asi que
        // `roles: ["admin"]` los bloquea y ninguna escritura llega a la base de
        // datos. `policy.serverContext()` compila a `rebase.uid() IS NULL`, cierto
        // unicamente en esos flujos internos: no abre nada a peticiones
        // externas, que siempre llevan un uid (real o anonimo).
        { operations: ["insert", "update", "delete"], condition: policy.serverContext() }
    ],
    callbacks: {
        afterSave: async ({ values, status, context }) => {
            if (status !== "new") return; // Only process on creation, not edits

            // `customer` is a relation, and afterSave is handed the saved ROW,
            // so the relation arrives as its derived foreign key, NOT as the
            // `customer` property that writers pass in. Reading only `customer`
            // made this callback return early on every real save:
            // lifetime_gross_spent never moved, so no purchase ever promoted
            // anyone. `customers.ts` has the same both-shapes handling.
            //
            // 0.14 renamed that foreign key on the wire to `customerId` — the
            // `customer_id` column is unchanged, but the column name is no
            // longer what the row is keyed by, and reading it reintroduced
            // exactly the early-return regression described above.
            // Writers may also hand back an expanded record, hence the `.id`.
            const valuesRow = values as unknown as Record<string, unknown>;
            const customerRelation = valuesRow.customer ?? valuesRow.customerId;
            const customerId = (
                typeof customerRelation === "object" && customerRelation !== null
                    ? (customerRelation as Record<string, unknown>).id
                    : customerRelation
            ) as string | undefined;
            if (!customerId) return;

            // Fetch current customer
            const customer = await context.data.collection("customers").findById(customerId);
            if (!customer) return;

            // 0.9 returns flat rows from context.data (no Snapshot `.values` wrapper).
            const customerValues = customer;
            const grossSpent = (customerValues.lifetime_gross_spent as number) ?? 0;
            const totalReturns = (customerValues.lifetime_returns as number) ?? 0;
            const type = values.type as string;
            const amount = Math.abs(values.gross_amount as number ?? 0);

            // A sale of the last 12 months imported from Odoo for a customer
            // whose lifetime totals already include it (the September 2026
            // import wrote them): it counts for the points, not twice in the
            // totals. The customer is still saved, which applies the rules.
            const metadata = (values.metadata ?? {}) as Record<string, unknown>;
            if (metadata.counted_in_lifetime === "already") {
                await context.data.collection("customers").update(customerId, {
                    evaluated_at: new Date().toISOString()
                });
            } else if (type === "purchase") {
                await context.data.collection("customers").update(customerId, {
                    lifetime_gross_spent: grossSpent + amount
                });
            } else if (type === "return") {
                await context.data.collection("customers").update(customerId, {
                    lifetime_returns: totalReturns + amount
                });
            }
        }
    },
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid",
            admin: { readOnly: true }
        },
        customer: {
            name: "Customer",
            type: "relation",
            validation: { required: true },
            relation: {
                kind: "belongsTo",
                target: () => customersCollection,
                // 0.18 made a required belongsTo default to ON DELETE RESTRICT.
                // The live database, the migration baseline and
                // tests/foreign-key-parity.test.ts all have CASCADE here, and a
                // boot-time ensure would rewrite the constraint in production on
                // the first deploy of that default. Written down so nothing moves;
                // whether the ledger should refuse a customer delete is a
                // decision to take on purpose, with a migration, not by omission.
                onDelete: "cascade",
            }
        },
        type: {
            name: "Type",
            type: "string",
            validation: { required: true },
            enum: [
                { id: "purchase", label: "Purchase", color: "green" },
                { id: "return", label: "Return", color: "red" }
            ]
        },
        source: {
            name: "Source",
            type: "string",
            validation: { required: true },
            enum: [
                { id: "shopify", label: "Shopify", color: "blue" },
                { id: "odoo_pos", label: "Odoo POS", color: "purple" },
                { id: "manual", label: "Manual", color: "gray" }
            ]
        },
        gross_amount: {
            name: "Gross Amount (€)",
            type: "number",
            validation: { required: true, min: 0 },
            description: "Absolute amount of the ticket/invoice in euros"
        },
        net_amount: {
            name: "Net Amount (€)",
            type: "number",
            description: "Net amount (negative for returns)"
        },
        points_delta: {
            name: "Points Δ",
            type: "number",
            description: "Points added (positive) or subtracted (negative). 1€ = 1 point."
        },
        external_order_id: {
            name: "External Order ID",
            type: "string",
            description: "Shopify order ID or Odoo invoice number"
        },
        external_refund_id: {
            name: "External Refund ID",
            type: "string",
            description: "Shopify refund ID or Odoo credit note number"
        },
        metadata: {
            name: "Metadata",
            type: "map",
            keyValue: true,
            description: "Raw webhook payload or additional context",
            admin: { hideFromCollection: true }
        },
        occurred_at: {
            name: "Date of the sale",
            type: "date",
            mode: "date_time",
            description: "When the sale or the return happened (the invoice or ticket date). Points expire 12 " +
                "months after it; empty means when it was recorded.",
        },
        processed_at: {
            name: "Processed At",
            type: "date",
            mode: "date_time",
            description: "When the tier engine processed this transaction"
        },
        created_at: {
            name: "Created At",
            type: "date",
            mode: "date_time",
            autoValue: "on_create",
            admin: { readOnly: true }
        }
    },
    admin: {
        icon: "Receipt",
        group: "Loyalty",
        defaultViewMode: "list",
        enabledViews: ["list", "table"],
        // What a transaction is for: how much, how many points it moved, and the
        // order it came from. Type and date alone never answered the question
        // anyone opens this table to ask.
        listProperties: ["type", "gross_amount", "points_delta", "external_order_id"],
        previewProperties: ["customer", "source"],
        openEntityMode: "side_panel",
        exportable: true,
        selectionEnabled: true,
        sort: ["created_at", "desc"],
        propertiesOrder: [
            "customer", "type", "source",
            "gross_amount", "net_amount", "points_delta",
            "external_order_id", "external_refund_id",
            "occurred_at", "processed_at", "metadata", "created_at"
        ]
    }
});

export default transactionsCollection;
